/**
 * Calage d'un plan fourni sur la carte.
 *
 * Un organisateur a presque toujours un plan — souvent une image d'illustration,
 * parfois un PDF d'imprimeur. Le caler consiste à désigner quelques repères
 * communs entre l'image et la carte, puis à en déduire la transformation qui
 * envoie l'une sur l'autre (CARTOGRAPHIE.md, niveau A).
 *
 * C'est le cas le plus fréquent, et de loin le moins coûteux : ni vol, ni
 * relevé terrain.
 */

/** Un repère : un point de l'image et le point du monde qui lui correspond. */
export type ControlPoint = {
  /** Position dans l'image, en pixels, origine en haut à gauche. */
  x: number;
  y: number;
  latitude: number;
  longitude: number;
};

/**
 * Transformation affine de l'image vers le monde.
 *
 *   longitude = a·x + b·y + c
 *   latitude  = d·x + e·y + f
 *
 * Une affine suffit ici : elle couvre translation, échelle, rotation et
 * cisaillement, c'est-à-dire tout ce qu'un plan dessiné à plat peut subir. Elle
 * ne corrige pas une perspective, mais un plan de festival n'en a pas.
 */
export type AffineTransform = {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
};

export type GeoreferenceResult = {
  transform: AffineTransform;
  /**
   * Écart entre chaque repère et sa position recalculée, en mètres.
   *
   * C'est le seul moyen de dire à l'organisateur si son calage tient : un repère
   * mal placé se voit immédiatement à son écart, là où une carte de travers ne
   * saute pas aux yeux.
   */
  residuals: number[];
  /** Écart quadratique moyen, en mètres. */
  rmse: number;
};

const M_PER_DEG_LAT = 111_320;

/** Résout un système 3×3 par élimination de Gauss avec pivot partiel. */
function solve3(
  matrix: readonly [number[], number[], number[]],
  rhs: readonly [number, number, number],
): [number, number, number] | null {
  const m = matrix.map((row, i) => [...(row as number[]), rhs[i] as number]);

  for (let col = 0; col < 3; col += 1) {
    // Pivot partiel : sans lui, trois repères presque alignés produisent des
    // divisions par des valeurs minuscules et une transformation absurde.
    let pivot = col;
    for (let row = col + 1; row < 3; row += 1) {
      if (
        Math.abs((m[row] as number[])[col] as number) >
        Math.abs((m[pivot] as number[])[col] as number)
      ) {
        pivot = row;
      }
    }

    if (Math.abs((m[pivot] as number[])[col] as number) < 1e-12) return null;
    [m[col], m[pivot]] = [m[pivot] as number[], m[col] as number[]];

    const pivotRow = m[col] as number[];
    const pivotValue = pivotRow[col] as number;

    for (let row = 0; row < 3; row += 1) {
      if (row === col) continue;
      const current = m[row] as number[];
      const factor = (current[col] as number) / pivotValue;
      for (let k = col; k < 4; k += 1) {
        current[k] = (current[k] as number) - factor * (pivotRow[k] as number);
      }
    }
  }

  return [0, 1, 2].map((i) => {
    const row = m[i] as number[];
    return (row[3] as number) / (row[i] as number);
  }) as [number, number, number];
}

/**
 * Ajuste une affine sur les repères, au sens des moindres carrés.
 *
 * Trois repères suffisent et donnent une solution exacte ; au-delà, l'ajustement
 * répartit l'erreur, ce qui vaut mieux que de faire confiance à trois clics.
 */
export function fitAffine(points: readonly ControlPoint[]): GeoreferenceResult | null {
  if (points.length < 3) return null;

  // Équations normales : on résout deux systèmes 3×3, un par coordonnée.
  let sxx = 0,
    sxy = 0,
    sx = 0,
    syy = 0,
    sy = 0,
    n = 0;
  let sxLon = 0,
    syLon = 0,
    sLon = 0;
  let sxLat = 0,
    syLat = 0,
    sLat = 0;

  for (const p of points) {
    sxx += p.x * p.x;
    sxy += p.x * p.y;
    sx += p.x;
    syy += p.y * p.y;
    sy += p.y;
    n += 1;

    sxLon += p.x * p.longitude;
    syLon += p.y * p.longitude;
    sLon += p.longitude;

    sxLat += p.x * p.latitude;
    syLat += p.y * p.latitude;
    sLat += p.latitude;
  }

  const normal: [number[], number[], number[]] = [
    [sxx, sxy, sx],
    [sxy, syy, sy],
    [sx, sy, n],
  ];

  const lon = solve3(normal, [sxLon, syLon, sLon]);
  const lat = solve3(normal, [sxLat, syLat, sLat]);

  // Repères alignés ou confondus : aucune transformation ne s'en déduit.
  if (!lon || !lat) return null;

  const transform: AffineTransform = {
    a: lon[0],
    b: lon[1],
    c: lon[2],
    d: lat[0],
    e: lat[1],
    f: lat[2],
  };

  const residuals = points.map((point) => {
    const projected = applyAffine(transform, point.x, point.y);
    const dLat = (projected.latitude - point.latitude) * M_PER_DEG_LAT;
    const dLon =
      (projected.longitude - point.longitude) *
      M_PER_DEG_LAT *
      Math.cos((point.latitude * Math.PI) / 180);
    return Math.hypot(dLat, dLon);
  });

  const rmse = Math.sqrt(
    residuals.reduce((sum, value) => sum + value * value, 0) / residuals.length,
  );

  return { transform, residuals, rmse };
}

/** Envoie un point de l'image sur le monde. */
export function applyAffine(
  transform: AffineTransform,
  x: number,
  y: number,
): { latitude: number; longitude: number } {
  return {
    longitude: transform.a * x + transform.b * y + transform.c,
    latitude: transform.d * x + transform.e * y + transform.f,
  };
}

/**
 * Qualité du calage, telle qu'on peut l'annoncer à un organisateur.
 *
 * Les seuils sont ceux d'un site de festival, pas d'un cadastre : à deux mètres
 * près on retrouve une tente, et un plan d'illustration ne fera jamais mieux
 * que quelques mètres puisqu'il n'est pas à l'échelle.
 */
export function describeAccuracy(
  rmse: number,
): "excellent" | "bon" | "approximatif" | "mauvais" {
  if (rmse <= 5) return "excellent";
  if (rmse <= 15) return "bon";
  if (rmse <= 40) return "approximatif";
  return "mauvais";
}

/**
 * Emprise géographique d'une image calée.
 *
 * Les quatre coins sont projetés, puis encadrés : une image pivotée ne donne pas
 * un rectangle aligné sur les méridiens, et prendre seulement deux coins
 * tronquerait le plan.
 */
export function affineBounds(
  transform: AffineTransform,
  width: number,
  height: number,
): { west: number; south: number; east: number; north: number } {
  const corners = [
    applyAffine(transform, 0, 0),
    applyAffine(transform, width, 0),
    applyAffine(transform, 0, height),
    applyAffine(transform, width, height),
  ];

  return {
    west: Math.min(...corners.map((c) => c.longitude)),
    east: Math.max(...corners.map((c) => c.longitude)),
    south: Math.min(...corners.map((c) => c.latitude)),
    north: Math.max(...corners.map((c) => c.latitude)),
  };
}

/**
 * Les quatre coins d'un plan calé, dans l'ordre attendu par MapLibre.
 *
 * Une image géoréférencée n'a pas besoin d'être découpée en tuiles pour être
 * affichée : MapLibre sait poser une image sur quatre coins. C'est exactement ce
 * qu'une transformation affine décrit, rotation comprise — un plan de travers
 * s'affiche donc de travers, comme il le doit.
 *
 * L'ordre est imposé par MapLibre : haut-gauche, haut-droit, bas-droit,
 * bas-gauche. Le permuter retourne le plan sans rien signaler.
 */
export type Corners = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

export function affineCorners(
  transform: AffineTransform,
  width: number,
  height: number,
): Corners {
  const at = (x: number, y: number): [number, number] => {
    const point = applyAffine(transform, x, y);
    return [point.longitude, point.latitude];
  };

  // Le quadruplet est typé : MapLibre en exige exactement quatre, et un tableau
  // de longueur libre laisserait passer un plan à trois coins.
  return [at(0, 0), at(width, 0), at(width, height), at(0, height)];
}
